import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { IoChevronBackCircle, IoChevronForwardCircle, IoEyeOutline } from "react-icons/io5";
import { format, subDays, getDaysInMonth } from "date-fns";
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { useUser } from '../../context/UserContext';
import { useNavigate } from "react-router";
import { IoSearchSharp } from 'react-icons/io5';


function MonthlyAttendanceReport() {
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize] = useState(10);
  const [exportloading, setExportLoading] = useState(false);

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [year, setYear] = useState(() => new Date().getFullYear());

  const today = new Date();
  const isCurrentMonthSelected =
    month === today.getMonth() + 1 && year === today.getFullYear();

  const yesterday = subDays(new Date(), 1);
  const searchTerm = searchParams.get("search") || "";

  // Popup modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { currentUser } = useUser();

  const navigate = useNavigate();


  // Pastel colors for each status
  const statusColors = {
    Present: "bg-green-200 text-green-800",
    Absent: "bg-red-200 text-red-800",
    HalfDay: "bg-orange-200 text-orange-800",
    CompOff: "bg-purple-200 text-purple-800",
    Late: "bg-yellow-200 text-yellow-800",
    EarlyOut: "bg-pink-200 text-pink-800",
    OnDuty: "bg-blue-200 text-blue-800",
    Holiday: "bg-teal-200 text-teal-800",
    CompOffApply: "bg-indigo-200 text-indigo-800",
    Pending: "bg-gray-200 text-gray-800",
    EarnedLeave: "bg-emerald-200 text-emerald-800",
    SickLeave: "bg-rose-200 text-rose-800",
    CompOffLeave: "bg-fuchsia-200 text-fuchsia-800",
  };

  // Days till yesterday
  const daysTillYesterday = (() => {
    const isSameMonth = yesterday.getMonth() + 1 === month && yesterday.getFullYear() === year;
    if (isSameMonth) {
      return yesterday.getDate();
    } else {
      return getDaysInMonth(new Date(year, month - 1));
    }
  })();

  const applySearch = () => {
    navigate(`?search=${searchInput}`);
    setCurrentPage(1);
  }

  const fetchReport = async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/daily-attendance/monthly-reports`, {
        params: {
          month,
          year,
          page: page - 1,
          size: pageSize,
          search: searchTerm,
        },
      });

      const data = response.data;
      setReport(data.content || []);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.number + 1 || 1);

      console.log("Fetched monthly attendance report:", data);
    } catch (error) {
      console.error('Failed to fetch monthly attendance report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (userId, employeeName) => {
    try {
      const res = await api.get(`/api/daily-attendance/get-monthly-data`, {
        params: { year, month, userId }
      });
      setAttendanceData(res.data);
      setSelectedEmployee(employeeName);
      setIsModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch user attendance details:", err);
    }
  };

  useEffect(() => {
    fetchReport(currentPage);
  }, [currentPage, month, year, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
    fetchReport(1);
  }, [searchParams]);

  const handleCurrentMonthDownload = async () => {
    

    setExportLoading(true);

    try {
      const response = await api.get(`/attendance/download?year=${year}&month=${month}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Attendance_${month}_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setIsExportModalOpen(false);
      toast.success("File downloaded successfully", { position: "bottom-right", autoClose: 3000 });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Error downloading file", { position: "bottom-right", autoClose: 3000 });
    } finally {
      setExportLoading(false);
    }
  };

  const handleDateRangeDownload = async () => {
 

    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates", { position: "bottom-right", autoClose: 3000 });
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date must be before end date", { position: "bottom-right", autoClose: 3000 });
      return;
    }

    setExportLoading(true);

    try {
      const response = await api.get(`/attendance-range?startDate=${startDate}&endDate=${endDate}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Attendance_${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setIsExportModalOpen(false);
      toast.success("File downloaded successfully", { position: "bottom-right", autoClose: 3000 });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Error downloading file", { position: "bottom-right", autoClose: 3000 });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-1 mb-4 bg-white rounded-2xl p-4">
        <h2 className="text-2xl font-bold text-slate-800">Monthly Attendance</h2>
<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 relative">
<div className="relative">
  <input
    type="text"
    placeholder="Search employee..."
    value={searchInput}
    onChange={(e) => setSearchInput(e.target.value)}   // only updates local state
    onKeyDown={(e) => {
      if (e.key === 'Enter') applySearch();             // apply on Enter
    }}
    className="pl-9 pr-4 py-2 text-sm rounded-full border border-slate-200 bg-slate-50 focus:outline-none focus:border-slate-400 w-56"
  />
  <IoSearchSharp
    onClick={applySearch}                               // apply on icon click
    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm cursor-pointer hover:text-slate-600"
  />
</div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExportModalOpen(true)}
            className="bg-green-600 rounded-full px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            Export
          </button>
          
          <button
            onClick={() =>
              setMonth((prevMonth) => {
                if (prevMonth === 1) {
                  setYear((prevYear) => prevYear - 1);
                  return 12;
                }
                return prevMonth - 1;
              })
            }
          >
            <IoChevronBackCircle className="text-xl text-slate-700" />
          </button>

          <span className="border px-6 py-2 rounded-full text-slate-700 text-sm">
            {format(new Date(year, month - 1), "MMM yyyy")}
          </span>

          <button
            onClick={() => {
              if (isCurrentMonthSelected) return;
              setMonth((prevMonth) => {
                if (prevMonth === 12) {
                  setYear((prevYear) => prevYear + 1);
                  return 1;
                }
                return prevMonth + 1;
              });
            }}
            disabled={isCurrentMonthSelected}
            className={isCurrentMonthSelected ? 'opacity-40' : ''}
          >
            <IoChevronForwardCircle className="text-xl text-slate-700" />
          </button>

          <span className="text-xs text-slate-500">
            Till {format(yesterday, 'dd MMM')}
          </span>

          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden ">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b">
              <tr>
                {['Sl','Employee ID', 'Employee Name', 'P', 'A', 'EL', 'SL', 'CL', 'CO', 'CP', 'Days', 'Hrs', 'OT',].map((h, i) => (
                  <th key={i} className="px-2 py-4 text-left text-sm font-semibold text-slate-700">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="13" className="px-2 py-4 text-center">
                    <svg className="animate-spin h-5 w-5 text-slate-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                  </td>
                </tr>
              ) : report.length === 0 ? (
                <tr>
                  <td colSpan="13" className="text-center py-4 text-slate-500 text-xs">No records found</td>
                </tr>
              ) : (
                report.map((record, index) => {
                  const workedDays = record.absentDays !== null ? Math.max(0, (daysTillYesterday - record.absentDays).toFixed(1)) : '-';

                  return (
                    <tr key={index}
                     title="Click to view attendance details"
                     onClick={() => handleView(record.employeeId, record.employeeName)}
                     className="hover:bg-slate-50 border-b border-slate-100">
                      <td className="px-3 py-2 text-sm">{(currentPage - 1) * pageSize + index + 1}</td>
                      <td className="px-3 py-2 text-sm font-medium">{record.employeeCode}</td>
                      <td className="px-3 py-2 text-sm font-medium">{record.employeeName}</td>
                      {/* <td className="px-2 py-1.5 text-sm">{record.department}</td> */}
                      <td className="px-3 py-2 text-sm">{record.presentDays ?? 0}</td>
                      <td className="px-3 py-2 text-sm">{record.absentDays ?? 0}</td>
                      <td className="px-3 py-2 text-sm">{record.earnedLeave ?? 0}</td>
                      <td className="px-3 py-2 text-sm">{record.sickLeave ?? 0}</td>
                      <td className="px-3 py-2 text-sm">{record.compOffLeave ?? 0}</td>
                      <td className="px-3 py-2 text-sm">{record.compOff ?? 0}</td>
                      <td className="px-3 py-2 text-sm">{record.compPays ?? 0}</td>
                      <td className="px-3 py-2 font-semibold text-green-700">{workedDays}/{getDaysInMonth(new Date(year, month - 1))}</td>
                      <td className="px-3 py-2 text-sm">{record.totalWorkedHours ?? 0}</td>
                      <td className="px-3 py-2 text-sm">{record.overtimeHours ?? 0}</td>
                      {/* <td className="px-2 py-1.5 text-sm">
                        <button
                          onClick={() => handleView(record.employeeId, record.employeeName)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <IoEyeOutline className="text-base" />
                        </button>
                      </td> */}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {report.length > 0 && (
            <div className="px-2 py-2 border-t">
              <Stack spacing={1} className="flex text-white justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  size="small"
                  showFirstButton
                  showLastButton 
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#475569",
                      borderColor: "#e2e8f0",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#475569 !important",
                      color: "#fff",
                    },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </div>

      {/* Export Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-4 max-w-md w-full mx-4 space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h2 className="text-base font-bold text-slate-800">Export Attendance</h2>
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleCurrentMonthDownload}
                disabled={exportloading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded text-sm disabled:opacity-50"
              >
                {exportloading ? 'Exporting...' : `Export ${format(new Date(year, month - 1), "MMMM yyyy")}`}
              </button>

              <div className="flex items-center gap-2">
                <div className="flex-1 border-t"></div>
                <span className="text-xs text-slate-400">OR</span>
                <div className="flex-1 border-t"></div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-medium text-slate-700">Custom Date Range</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-2 py-1.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-2 py-1.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>
              </div>

              <button
                onClick={handleDateRangeDownload}
                disabled={exportloading || !startDate || !endDate}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded text-sm disabled:opacity-50"
              >
                {exportloading ? 'Exporting...' : 'Export Date Range'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-4 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b pb-2 mb-3">
              <h2 className="text-sm font-semibold">{selectedEmployee}</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-1 text-xs mb-3">
              {Object.entries(statusColors).map(([status, colorClass]) => (
                <div key={status} className="flex items-center gap-1">
                  <span className={`w-3 h-3 rounded ${colorClass}`}></span>
                  <span className="text-xs">{status}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {attendanceData.map((day) => (
                <div
                  key={day.id}
                  className={`p-1.5 text-center rounded text-xs ${statusColors[day.status] || "bg-gray-200 text-gray-800"}`}
                  title={day.status}
                >
                  {new Date(day.date).getDate()}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthlyAttendanceReport;